import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replohq/lattice/components/table/Table";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { useRateCards } from "@v2/features/agent-billing/hooks/useRateCards";
import { agentBillingRouter } from "@v2/trpc/routes/agentBilling";

import { ALWAYS_FRESH, formatDate, useCapacityUsage } from "./shared";

function formatValue(value: number | null | undefined): string {
  return value === null ? "∞" : (value?.toString() ?? "--");
}

export const CapacityBalancesTable: React.FC = () => {
  const { rateCards } = useRateCards();
  const capacityUsage = useCapacityUsage();

  const allFeatures = rateCards?.[0]?.features ?? [];
  const capacityFeatures = allFeatures.filter(
    (feature) => feature.featureType !== "credits",
  );

  if (capacityFeatures.length === 0) {
    return (
      <div className="text-muted py-3 text-center text-xs">
        No capacity features.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Feature</TableHead>
          <TableHead>Usage</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {capacityFeatures.map((feature) => {
          const usage = capacityUsage[feature.featureType];
          return (
            <TableRow key={feature.featureType}>
              <TableCell>
                <span className="font-mono text-xs">{feature.featureType}</span>
              </TableCell>
              <TableCell>
                <span className="font-mono">
                  {usage
                    ? `${usage.current} / ${formatValue(usage.maximum)}`
                    : "--"}
                </span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export const CreditBalancesTable: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const { data, isLoading: isBalancesLoading } =
    agentBillingRouter.credits.find.useQuery(
      workspaceId ? { workspaceId } : skipToken,
      ALWAYS_FRESH,
    );

  if (isBalancesLoading) {
    return <div className="text-muted py-3 text-xs">Loading...</div>;
  }

  const creditBalances = data?.creditBalances ?? [];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Feature</TableHead>
          <TableHead>Balance Type</TableHead>
          <TableHead>Interval</TableHead>
          <TableHead>Balance</TableHead>
          <TableHead>Expires At</TableHead>
          <TableHead>Reference</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {creditBalances.map((creditBalance) => (
          <TableRow key={creditBalance.id}>
            <TableCell>
              <span className="font-mono text-xs">
                {creditBalance.featureType}
              </span>
            </TableCell>
            <TableCell>
              <Tag variant="neutral" size="sm">
                {creditBalance.type}
              </Tag>
            </TableCell>
            <TableCell>
              <span className="font-mono text-xs">
                {creditBalance.interval}
              </span>
            </TableCell>
            <TableCell>
              <span className="font-mono">
                {formatValue(creditBalance.balance)}
              </span>
            </TableCell>
            <TableCell>
              <span className="whitespace-nowrap text-xs">
                {formatDate(creditBalance.expiresAt)}
              </span>
            </TableCell>
            <TableCell>
              <span className="block max-w-[200px] truncate text-xs">
                {creditBalance.referenceId ?? "--"}
              </span>
            </TableCell>
          </TableRow>
        ))}
        {creditBalances.length === 0 && (
          <TableRow>
            <TableCell colSpan={6}>
              <span className="text-muted block py-3 text-center text-xs">
                No credit balances.
              </span>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
